import { useMemo, useState } from 'react';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { formatINR } from '@/shared/utils/currency';
import { useCustomerAging, useVendorAging, type AgingRow } from '../hooks/useReports';
import { ReportEmpty, ReportProblem } from '../components/ReportState';
import { Search, Users } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Receivables & Payables Ageing — read-only screens over
// GET /api/accounts/reports/customer-aging and …/vendor-aging.
//
// Both endpoints group unpaid, partially-paid and overdue sales invoices
// (customer-aging) or purchase bills (vendor-aging) by counterparty, then bucket
// each document's amountDue by how far past its own dueDate it sits. The buckets
// and the totals are the server's arithmetic; nothing here re-derives an age or
// invents a due date. The figures below the table are the sum of the rows the
// server actually returned, shown for the rows currently visible after the
// search filter.
// ─────────────────────────────────────────────────────────────────────────────

const BUCKETS = [
  { key: 'current', label: 'Not yet due' },
  { key: 'days0_30', label: '0–30 days' },
  { key: 'days30_60', label: '31–60 days' },
  { key: 'days60_90', label: '61–90 days' },
  { key: 'days90Plus', label: '90+ days' },
] as const;

type BucketKey = (typeof BUCKETS)[number]['key'];

const money = (value: number | null | undefined) => formatINR(value ?? 0);

function sumBy(rows: AgingRow[], key: BucketKey | 'total'): number {
  return rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
}

function AgingTable({ rows, partyLabel }: { rows: AgingRow[]; partyLabel: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead className="sticky top-0 bg-slate-50/80 text-xs font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-900/80 dark:text-slate-400">
          <tr>
            <th className="px-4 py-3">{partyLabel}</th>
            {BUCKETS.map((b) => <th key={b.key} className="px-4 py-3 text-right">{b.label}</th>)}
            <th className="px-4 py-3 text-right">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40">
              <td className="max-w-[280px] truncate px-4 py-2.5 font-medium">{r.name || 'Unlinked party'}</td>
              {BUCKETS.map((b) => (
                <td key={b.key} className={`px-4 py-2.5 text-right font-mono text-xs ${b.key === 'days90Plus' && r.days90Plus > 0 ? 'font-bold text-rose-600' : ''}`}>
                  {r[b.key] > 0 ? money(r[b.key]) : <span className="text-slate-300 dark:text-slate-700">—</span>}
                </td>
              ))}
              <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold">{money(r.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 bg-slate-50 font-semibold dark:bg-slate-900">
          <tr>
            <td className="px-4 py-3 text-right uppercase tracking-wider">{`Total (${rows.length} ${partyLabel.toLowerCase()}s)`}</td>
            {BUCKETS.map((b) => <td key={b.key} className="px-4 py-3 text-right font-mono text-xs">{money(sumBy(rows, b.key))}</td>)}
            <td className="px-4 py-3 text-right font-mono text-xs">{money(sumBy(rows, 'total'))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function AgingLoading() {
  return (
    <div className="space-y-4 py-4">
      <Skeleton className="h-24 w-full rounded-2xl" />
      <Skeleton className="h-72 w-full rounded-2xl" />
    </div>
  );
}

/** The subset of a react-query result these panels render from. */
interface AgingQuery {
  isLoading: boolean;
  error: unknown;
  data: AgingRow[] | undefined;
}

function AgingReport({
  query, kind,
}: {
  query: AgingQuery;
  kind: 'customer' | 'vendor';
}) {
  const isCustomer = kind === 'customer';
  const [search, setSearch] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const rows = useMemo<AgingRow[]>(() => {
    const all = query.data ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((r) => {
      if (term && !(r.name || '').toLowerCase().includes(term)) return false;
      if (overdueOnly && (r.current || 0) >= (r.total || 0)) return false;
      return true;
    });
  }, [query.data, search, overdueOnly]);

  return (
    <div className="space-y-4">
      {query.isLoading ? <AgingLoading />
        : query.error ? <ReportProblem error={query.error} subject={isCustomer ? 'the customer ageing report' : 'the vendor ageing report'} />
        : !query.data ? <Card><CardBody><ReportEmpty text="The report returned no rows." /></CardBody></Card>
        : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: `Total ${isCustomer ? 'Receivable' : 'Payable'}`, value: money(sumBy(query.data, 'total')) },
                { label: 'Overdue (past due date)', value: money(sumBy(query.data, 'days0_30') + sumBy(query.data, 'days30_60') + sumBy(query.data, 'days60_90') + sumBy(query.data, 'days90Plus')) },
                { label: 'Beyond 90 days', value: money(sumBy(query.data, 'days90Plus')) },
              ].map((f) => (
                <div key={f.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{f.label}</p>
                  <p className="mt-1 font-mono text-lg font-bold text-slate-900 dark:text-slate-100">{f.value}</p>
                </div>
              ))}
            </div>

            <Card className="overflow-hidden">
              <div className="flex flex-col gap-3 border-b bg-slate-50/70 p-4 dark:bg-slate-900/60 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${isCustomer ? 'customer' : 'vendor'} name…`}
                    className="pl-9 text-xs"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                  Only parties with something past their due date
                </label>
              </div>
              <CardBody className="p-0">
                {query.data.length === 0
                  ? <ReportEmpty text={`No ${isCustomer ? 'customer invoices' : 'vendor bills'} are unpaid, partially paid or overdue right now.`} />
                  : rows.length === 0
                    ? <ReportEmpty text={`Nothing matched ${search.trim() ? `“${search.trim()}”` : 'this filter'}.`} />
                    : <AgingTable rows={rows} partyLabel={isCustomer ? 'Customer' : 'Vendor'} />}
              </CardBody>
            </Card>
          </>
        )}
    </div>
  );
}

function CustomerAgingPanel() {
  const query = useCustomerAging();
  return <AgingReport kind="customer" query={{ isLoading: query.isLoading, error: query.error, data: query.data }} />;
}

function VendorAgingPanel() {
  const query = useVendorAging();
  return <AgingReport kind="vendor" query={{ isLoading: query.isLoading, error: query.error, data: query.data }} />;
}

export function AgingReportsPage() {
  const [tab, setTab] = useState<'customer' | 'vendor'>('customer');

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Receivables &amp; Payables Ageing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What is owed to us and what we owe, bucketed by how far past the document's own due date it sits.
        </p>
      </div>
      <ModuleGuide
        summary="Only unpaid, partially-paid and overdue documents appear. The buckets are the server's arithmetic against each document's due date — a figure here is never estimated."
        steps={[
          'Sales invoices (unpaid / partially paid / overdue) drive the customer ageing.',
          'Purchase bills drive the vendor ageing.',
          'Each document\'s outstanding amount lands in one bucket, from "not yet due" to "90+ days".',
        ]}
        formulas={[
          { label: 'Bucket', formula: 'days past dueDate, from 0–30 up to 90+' },
          { label: 'Total', formula: 'sum of amountDue across that party\'s open documents' },
        ]}
      />

      <div className="flex gap-2 border-b pb-1">
        {([
          { id: 'customer', label: 'Customer Ageing (Receivable)' },
          { id: 'vendor', label: 'Vendor Ageing (Payable)' },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === t.id
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-transparent text-muted-foreground hover:bg-muted/30 hover:text-foreground'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* One panel at a time, and keyed, so switching sides cannot leave the
          previous report's search text or filter applied to a different party
          list — and the side you are not looking at is never fetched. */}
      {tab === 'customer' ? <CustomerAgingPanel key="customer" /> : <VendorAgingPanel key="vendor" />}
    </div>
  );
}
